// src/components/TwoStepSetup.js - RULING 303 (D4). The administrator, a General Chairperson or a Local Chairperson
// turns on authenticator-app two-step sign-in for his own account. A link in the sidebar footer, not a screen (ruling
// 170's screen list and the handbooks' tables are unchanged). Turning it on is his choice in this phase; nobody is
// required to use it until Bob has enrolled first (ruling 303).
import React, { useState } from 'react';
import { multiFactor, TotpMultiFactorGenerator } from 'firebase/auth';
import { auth } from '../firebase';
import { mayEnrollTwoStep, cleanCode, twoStepError, TWO_STEP_ISSUER } from '../data/twoStep';

export default function TwoStepSetup({ user }) {
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  if (!mayEnrollTwoStep(user) || !auth.currentUser) return null;
  const enrolled = multiFactor(auth.currentUser).enrolledFactors.some((f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  const link = { fontSize: 11, color: '#8AA4BF', cursor: 'pointer', textDecoration: 'underline' };

  if (enrolled && !open) return <div style={{ marginTop: 8, fontSize: 11, color: '#40C070' }}>Two-step sign-in: ON</div>;
  if (!open) {
    return (
      <div style={{ marginTop: 8 }}>
        <span role="button" tabIndex={0} style={link} onClick={() => setOpen(true)} onKeyDown={(e) => e.key === 'Enter' && setOpen(true)}>
          Turn on two-step sign-in
        </span>
      </div>
    );
  }
  const start = async () => {
    setBusy(true); setErr('');
    try {
      const session = await multiFactor(auth.currentUser).getSession();
      setSecret(await TotpMultiFactorGenerator.generateSecret(session));
    } catch (e) { setErr(twoStepError(e && e.code)); } finally { setBusy(false); }
  };
  const finish = async () => {
    const c = cleanCode(code);
    if (!c) { setErr(twoStepError('auth/missing-code')); return; }
    setBusy(true); setErr('');
    try {
      await multiFactor(auth.currentUser).enroll(TotpMultiFactorGenerator.assertionForEnrollment(secret, c), 'Authenticator app');
      setMsg('Two-step sign-in is ON. From now on you will be asked for a code from your app when you sign in. If the platform asks you to sign in again now, that is expected.');
      setSecret(null); setCode('');
    } catch (e) { setErr(twoStepError(e && e.code)); } finally { setBusy(false); }
  };
  const otpauth = secret ? secret.generateQrCodeUrl(auth.currentUser.email || 'account', TWO_STEP_ISSUER) : '';
  return (
    <div style={{ marginTop: 8, fontSize: 11, color: '#C9D6E3', lineHeight: 1.5 }}>
      {msg ? <div style={{ color: '#40C070' }}>{msg}</div> : !secret ? (
        <div>
          <div>You need an authenticator app on your phone (Google Authenticator, Microsoft Authenticator or similar).</div>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 6 }} disabled={busy} onClick={start}>{busy ? 'Starting…' : 'Start'}</button>
        </div>
      ) : (
        <div>
          <div>1. In your app, add an account. On this phone, tap: <a href={otpauth} style={{ color: '#E8A020' }}>open in authenticator</a>. On another device, choose "enter a setup key" and type:</div>
          <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#E8A020', wordBreak: 'break-all', margin: '4px 0' }}>{secret.secretKey}</div>
          <div>2. Type the six-digit code the app shows:</div>
          <input aria-label="The six-digit code from your authenticator app" className="form-input" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !busy && finish()} style={{ marginTop: 4, padding: 6, fontSize: 13 }} />
          <button className="btn btn-primary btn-sm" style={{ marginTop: 6 }} disabled={busy} onClick={finish}>{busy ? 'Checking…' : 'Turn it on'}</button>
        </div>
      )}
      {err ? <div style={{ color: '#E06C6C', marginTop: 4 }}>{err}</div> : null}
      <div style={{ marginTop: 4 }}><span role="button" tabIndex={0} style={link} onClick={() => { setOpen(false); setSecret(null); setErr(''); setMsg(''); }}
        onKeyDown={(e) => e.key === 'Enter' && setOpen(false)}>Close</span></div>
    </div>
  );
}
